import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTheme } from "@mui/material/styles";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useNavigate } from "react-router-dom";
import { getInitials } from "../utils/utils";
import bagIcon from "../assets/Images/bag-icon.svg";
import locationIcon from "../assets/Images/location-icon.svg";
import clockIcon from "../assets/Images/clock-icon.svg";

const DeveloperCard = ({ developer }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          transform: "translateY(-4px)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 0,
          "&:last-child": { pb: 0 },
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {/* Top Section */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                backgroundColor: "hsl(222 47% 20%)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
              }}
            >
              {getInitials(developer.name)}
            </Box>

            <Box>
              <Typography fontWeight={600}>{developer.name}</Typography>
              <Typography
                variant="body2"
                color={theme.palette.custom.developerRoleColor}
              >
                {developer?.expertise?.name}
              </Typography>
            </Box>
          </Stack>

          {/* Meta */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            justifyContent="start"
            my={2}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <img src={bagIcon} alt="Bag Icon" />
              <Typography variant="body2" color="text.secondary">
                {developer.experience} years
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <img src={locationIcon} alt="Location Icon" />
              <Typography variant="body2" color="text.secondary">
                {developer?.location || developer?.nationality?.name || ""}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <img src={clockIcon} alt="Clock Icon" />
              {developer?.timeZone ? (
                <Typography variant="body2" color="text.secondary">
                  {developer?.timeZone}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {developer?.timeFrom || "00:00"} -{" "}
                  {developer?.timeTill || "00:00"}{" "}
                </Typography>
              )}
            </Stack>
          </Stack>

          {/* Tags */}
          <Stack direction="row" spacing={1.5} alignItems="center" my={2}>
            <Chip
              label={developer?.availability || "Full-time"}
              size="small"
              sx={{
                borderRadius: "999px",
                backgroundColor: "#DCFCE7", // light green
                color: "#166534", // dark green text
                fontWeight: 500,
              }}
            />

            {/* <Chip
              label={developer?.area || "Cross-platform"}
              size="small"
              sx={{
                borderRadius: "999px",
                backgroundColor: theme.palette.background.muted,
                color: theme.palette.text.muted,
                fontWeight: 500,
              }}
            /> */}
          </Stack>

          {/* Skills */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap my={2}>
            {developer.skills.slice(0, 5).map((skill, index) => (
              <Chip
                key={index}
                label={skill}
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  borderRadius: "999px",
                  backgroundColor: theme.palette.background.muted,
                  border: `1px solid ${theme.palette.custom.developerBorderColor}`,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  px: 1,
                }}
              />
            ))}
          </Stack>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "1.625",
              typography: "textSm",
            }}
          >
            {developer?.summary || "No summary available"}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            cursor: "pointer",
            px: 2,
            py: 1,
            borderRadius: 2,
            "&:hover": {
              backgroundColor: theme.palette.background.muted,
            },
          }}
          onClick={() => navigate(`/engineer/${developer._id}`)}
        >
          <Typography
            fontWeight={500}
            sx={{
              typography: "textSm",
            }}
          >
            View Full Profile
          </Typography>
          <ArrowForwardIcon fontSize="small" />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeveloperCard;
